<script setup lang="ts">
/* =============================================================
   DOCKING LOCATION — CREATE / EDIT
   =============================================================
   The owner's version of admin/src/pages/BerthFormPage.vue, and
   the validation is that page's, adapted: same CHECK constraints
   underneath, same messages on the field that broke one.

   Two differences. There is no owner select -- a berth made here
   belongs to the signed-in user's company, and RLS would refuse
   any other. And the position is picked on a map: an owner knows
   where their quay is, not its coordinates. The lat/lng fields
   stay, in sync with the pin, for anyone pasting from a chart.

   A new berth lands on its Rates tab, because a berth without a
   price cannot be booked and setting one is the obvious next step.
   ============================================================= */

import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import SelectField from '@/components/ui/SelectField.vue'
import CountrySelect from '@/components/ui/CountrySelect.vue'
import AmenityPicker from '@/components/ui/AmenityPicker.vue'
import Alert from '@/components/ui/Alert.vue'
import type { MapPoint } from '@/lib/map'
import { routes, to } from '@/config/routes'
import { createBerth, getBerth, listBerths, updateBerth } from '@/lib/berths'
import { useAuth } from '@/lib/auth/useAuth'
import { errorMessage } from '@/lib/errors'
import { BOOKING_MODES } from '@/lib/bookingLabels'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { AmenityCode, Berth, BerthInput, BookingMode } from '@/lib/types'

const MapView = defineAsyncComponent(() => import('@/components/MapView.vue'))

const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const editingId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editingId.value !== null)

interface FormState {
  name: string
  city: string
  country_code: string
  latitude: string
  longitude: string
  amenities: AmenityCode[]
  /** Blank means "not stated", which is a real answer -- see validate(). */
  max_length_m: string
  max_width_m: string
  max_draft_m: string
  max_vessels: string
  booking_mode: BookingMode
  /** Only shown, and only sent, under manual. */
  response_hours: string
}

const blank: FormState = {
  name: '',
  city: '',
  country_code: '',
  latitude: '',
  longitude: '',
  amenities: [],
  max_length_m: '',
  max_width_m: '',
  max_draft_m: '',
  max_vessels: '',
  booking_mode: 'instant',
  response_hours: '',
}

const form = ref<FormState>({ ...blank })
/** What was loaded, to tell a real edit from a visit. */
const pristine = ref<FormState>({ ...blank })
const heading = ref('')

useDocumentTitle(() => (isEdit.value ? `Edit ${heading.value || 'location'}` : 'Add location'))

const loading = ref(false)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))

const optional = (n: number | null) => (n === null ? '' : String(n))

/* The company's other berths, greyed on the picker: the map opens on the
   owner's own stretch of river instead of all of Europe, and a new pier is
   placed relative to the old ones. Nice to have, so a failure is silent. */
const neighbours = ref<Berth[]>([])

onMounted(async () => {
  const companyId = user.value?.companyId
  if (companyId !== undefined)
    listBerths(companyId)
      .then((rows) => (neighbours.value = rows.filter((b) => b.id !== editingId.value)))
      .catch(() => {})
  if (!isEdit.value) return
  loading.value = true
  try {
    const berth = await getBerth(editingId.value as number)
    // Someone else's berth is readable (search needs that) but not editable,
    // and saying "not found" beats a form whose save RLS will refuse.
    if (!berth || berth.company_id !== user.value?.companyId) {
      notFound.value = true
      return
    }
    const state: FormState = {
      name: berth.name,
      city: berth.city,
      country_code: berth.country_code,
      latitude: String(berth.latitude),
      longitude: String(berth.longitude),
      amenities: [...berth.amenities],
      max_length_m: optional(berth.max_length_m),
      max_width_m: optional(berth.max_width_m),
      max_draft_m: optional(berth.max_draft_m),
      max_vessels: optional(berth.max_vessels),
      booking_mode: berth.booking_mode,
      response_hours: optional(berth.response_hours),
    }
    form.value = { ...state }
    pristine.value = { ...state }
    heading.value = berth.name
  } catch (e) {
    formError.value = errorMessage(e, 'Could not load the location.')
  } finally {
    loading.value = false
  }
})

/* --- position ------------------------------------------------------------
   The pin follows the two text fields, and a click on the map writes them.
   Six decimals is about 10 cm -- finer than any quay needs, and short enough
   to read. */

const pinned = computed<{ lat: number; lng: number } | null>(() => {
  const lat = Number(form.value.latitude)
  const lng = Number(form.value.longitude)
  if (form.value.latitude.trim() === '' || form.value.longitude.trim() === '') return null
  if (Number.isNaN(lat) || Number.isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
})

const pickPoints = computed<MapPoint[]>(() => [
  ...neighbours.value.map<MapPoint>((b) => ({
    id: b.id,
    lat: b.latitude,
    lng: b.longitude,
    title: b.name,
    tone: 'muted',
  })),
  ...(pinned.value
    ? [{ id: 'pin', ...pinned.value, title: form.value.name.trim() || 'New location', tone: 'selected' as const }]
    : []),
])

function onPick(lat: number, lng: number): void {
  form.value.latitude = lat.toFixed(6)
  form.value.longitude = lng.toFixed(6)
  delete errors.value.position
  delete errors.value.latitude
  delete errors.value.longitude
}

/* --- validation ----------------------------------------------------------
   Mirrors the CHECK constraints on public.berths. The database is still the
   authority -- this only puts a typo in the field it belongs to. */

function optionalMetres(raw: string, min: number): number | null | undefined {
  const v = raw.trim()
  if (v === '') return null
  const n = Number(v)
  return Number.isNaN(n) || n <= min ? undefined : n
}

function validate(): BerthInput | null {
  const next: Record<string, string> = {}

  const name = form.value.name.trim()
  if (!name) next.name = 'Enter a name.'

  const city = form.value.city.trim()
  if (!city) next.city = 'Enter a city.'

  const country_code = form.value.country_code
  if (!country_code) next.country_code = 'Choose a country.'

  const lat = Number(form.value.latitude)
  if (form.value.latitude.trim() === '' || Number.isNaN(lat)) next.latitude = 'Enter a number.'
  else if (lat < -90 || lat > 90) next.latitude = 'Must be between -90 and 90.'

  const lng = Number(form.value.longitude)
  if (form.value.longitude.trim() === '' || Number.isNaN(lng)) next.longitude = 'Enter a number.'
  else if (lng < -180 || lng > 180) next.longitude = 'Must be between -180 and 180.'

  if (form.value.latitude.trim() === '' && form.value.longitude.trim() === '')
    next.position = 'Click the map to place the berth.'

  const companyId = user.value?.companyId
  if (companyId === undefined) next.form = 'Your account is not linked to a company.'

  /* All four limits are optional: blank means "not stated". The 5 m floor on
     length mirrors the CHECK, which exists to catch ship counts typed into
     the length column. */
  const len = optionalMetres(form.value.max_length_m, 5)
  if (len === undefined) next.max_length_m = 'A length in metres over 5, or blank.'
  const width = optionalMetres(form.value.max_width_m, 0)
  if (width === undefined) next.max_width_m = 'A width in metres, or blank.'
  const draft = optionalMetres(form.value.max_draft_m, 0)
  if (draft === undefined) next.max_draft_m = 'A depth in metres, or blank.'

  const rawVessels = form.value.max_vessels.trim()
  const vessels = rawVessels === '' ? null : Number(rawVessels)
  if (vessels !== null && (!Number.isInteger(vessels) || vessels < 1))
    next.max_vessels = 'A whole number of vessels, or blank.'

  /* Manual needs a response time and instant must not have one --
     berths_response_hours_iff_manual. Under instant the field is hidden and
     clean() in berths.ts drops whatever it held. */
  const booking_mode = form.value.booking_mode
  const rawResponse = form.value.response_hours.trim()
  const response = rawResponse === '' ? null : Number(rawResponse)
  if (booking_mode === 'manual' && (response === null || !Number.isInteger(response) || response < 1))
    next.response_hours = 'Enter a whole number of hours.'

  // Every check above, then publish and decide -- writing to `next` after
  // this point would neither show nor stop the submit.
  errors.value = next
  if (next.form) formError.value = next.form
  if (Object.keys(next).length > 0) return null

  return {
    name,
    city,
    country_code,
    latitude: lat,
    longitude: lng,
    company_id: companyId as number,
    amenities: form.value.amenities,
    max_length_m: len as number | null,
    max_width_m: width as number | null,
    max_draft_m: draft as number | null,
    max_vessels: vessels,
    booking_mode,
    response_hours: booking_mode === 'manual' ? response : null,
  }
}

/* After the first failed submit the messages follow the typing, so a fixed
   field stops being red without another press of the button. */
const attempted = ref(false)
watch(form, () => attempted.value && validate(), { deep: true })

async function onSubmit(): Promise<void> {
  if (saving.value) return
  attempted.value = true
  const input = validate()
  if (!input) return

  saving.value = true
  formError.value = ''
  try {
    const saved = isEdit.value
      ? await updateBerth(editingId.value as number, input)
      : await createBerth(input)
    // Clear the guard before navigating, or leaving asks about changes that
    // were just written.
    pristine.value = { ...form.value }
    await router.push(isEdit.value ? to.berth(saved.id) : to.berthTab(saved.id, 'rates'))
  } catch (e) {
    formError.value = errorMessage(e, 'Could not save the location.')
  } finally {
    saving.value = false
  }
}

const cancelTo = computed(() =>
  isEdit.value ? to.berth(editingId.value as number) : routes.locations,
)

useUnsavedGuard(dirty, saving)
</script>

<template>
  <PageHeader
    :title="isEdit ? `Edit ${heading || 'location'}` : 'Add location'"
    :description="isEdit ? undefined : 'Where the berth is, which ships fit, and how it is booked.'"
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That location does not exist. <RouterLink :to="routes.locations">Back to your locations</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <section class="section">
        <h2 class="sectionTitle">Location</h2>
        <div class="form-grid">
          <TextField
            v-model="form.name"
            label="Name"
            :error="errors.name"
            placeholder="Riverside Pier 3"
            autocomplete="off"
            class="form-wide"
            required
          />
          <TextField
            v-model="form.city"
            label="City"
            :error="errors.city"
            placeholder="Bernkastel-Kues"
            autocomplete="off"
            required
          />
          <CountrySelect v-model="form.country_code" :error="errors.country_code" />

          <div class="form-wide picker">
            <div class="pickerHead">
              <span class="pickerLabel">Position</span>
              <span class="pickerHint" :class="{ pickerError: errors.position }">
                {{ errors.position ?? (pinned ? 'Click the map to move the pin.' : 'Click the map where the berth is.') }}
              </span>
            </div>
            <MapView :points="pickPoints" height="20rem" pickable @pick="onPick" />
          </div>

          <TextField
            v-model="form.latitude"
            label="Latitude"
            :error="errors.latitude"
            inputmode="decimal"
            placeholder="49.9161"
            autocomplete="off"
          />
          <TextField
            v-model="form.longitude"
            label="Longitude"
            :error="errors.longitude"
            inputmode="decimal"
            placeholder="7.0716"
            autocomplete="off"
          />
        </div>
      </section>

      <section class="section">
        <h2 class="sectionTitle">Which ships fit</h2>
        <p class="sectionHint">All optional. Leave blank what you do not want to state.</p>
        <div class="limits">
          <TextField
            v-model="form.max_length_m"
            label="Max length (m)"
            :error="errors.max_length_m"
            inputmode="decimal"
            placeholder="135"
            autocomplete="off"
          />
          <TextField
            v-model="form.max_width_m"
            label="Max width (m)"
            :error="errors.max_width_m"
            inputmode="decimal"
            placeholder="11.4"
            autocomplete="off"
          />
          <TextField
            v-model="form.max_draft_m"
            label="Max draft (m)"
            :error="errors.max_draft_m"
            inputmode="decimal"
            placeholder="2.5"
            autocomplete="off"
          />
          <TextField
            v-model="form.max_vessels"
            label="Vessels at once"
            :error="errors.max_vessels"
            inputmode="numeric"
            placeholder="2"
            autocomplete="off"
          />
        </div>
      </section>

      <section class="section">
        <h2 class="sectionTitle">Services</h2>
        <AmenityPicker v-model="form.amenities" label="Amenities" hint="What ships can use at this berth." />
      </section>

      <section class="section">
        <h2 class="sectionTitle">Booking</h2>
        <div class="form-grid">
          <SelectField
            v-model="form.booking_mode"
            label="Booking mode"
            :hint="BOOKING_MODES.find((m) => m.code === form.booking_mode)?.hint"
          >
            <option v-for="m in BOOKING_MODES" :key="m.code" :value="m.code">{{ m.label }}</option>
          </SelectField>
          <TextField
            v-if="form.booking_mode === 'manual'"
            v-model="form.response_hours"
            label="Usually answers within (hours)"
            :error="errors.response_hours"
            hint="Shown to ship operators when they request."
            inputmode="numeric"
            placeholder="24"
            autocomplete="off"
          />
        </div>
      </section>

      <footer class="form-actions">
        <Button variant="ghost" :to="cancelTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">
          {{ isEdit ? 'Save changes' : 'Add location' }}
        </Button>
      </footer>
    </form>
  </Panel>
</template>

<style scoped>
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.section + .section {
  padding-top: var(--space-md);
  border-top: var(--border-width) solid var(--color-border);
}

.sectionTitle {
  font-size: var(--text-base);
  letter-spacing: var(--tracking-tight);
}

.sectionHint {
  margin-top: calc(-1 * var(--space-xs));
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.limits {
  display: grid;
  gap: var(--space-sm) var(--space-md);
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 8.5rem), 1fr));
  align-items: start;
}

.picker {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.pickerHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3xs) var(--space-sm);
}

.pickerLabel {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.pickerHint {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.pickerError {
  color: var(--color-danger);
}

.picker :deep(.wrap) {
  border: var(--border-width) solid var(--color-border);
}
</style>
